import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Heart, Trash2, ShoppingBag, ArrowLeft, User, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/context/AuthContext";
import PreOrderModal from "@/components/PreOrderModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SavedProducts = () => {
  const navigate = useNavigate();
  const { customer, isLoggedIn, logout, getToken, unsaveProduct } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }
    fetchSavedProducts();
  }, [isLoggedIn, navigate]);

  const fetchSavedProducts = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/customer/saved`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching saved products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnsave = async (productId) => {
    try {
      await unsaveProduct(productId);
      setProducts(products.filter(p => p.id !== productId));
      toast.success("Product removed from saved");
    } catch (error) {
      toast.error("Failed to remove product");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  if (!isLoggedIn) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="saved-products-page">
      <Header />

      <section className="py-8 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* User Info */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-foreground">
                  Hello, {customer?.name}
                </h1>
                <p className="text-muted-foreground">{customer?.email}</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="rounded-full"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>

          {/* Saved Products */}
          <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <Heart className="w-6 h-6 text-primary fill-primary" />
              <h2 className="font-heading text-xl font-semibold text-foreground">
                Saved Products ({products.length})
              </h2>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-muted rounded-xl p-4">
                    <div className="aspect-square skeleton rounded-lg mb-4" />
                    <div className="h-5 skeleton rounded w-3/4 mb-2" />
                    <div className="h-4 skeleton rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <Heart className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground text-lg mb-4">No saved products yet</p>
                <Link to="/">
                  <Button className="rounded-full bg-primary hover:bg-primary/90">
                    Browse Products
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                  <div 
                    key={product.id} 
                    className="bg-muted/30 rounded-xl overflow-hidden border border-border/50 group"
                    data-testid={`saved-product-${product.id}`}
                  >
                    <div className="aspect-square relative overflow-hidden">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                      <button
                        onClick={() => handleUnsave(product.id)}
                        className="absolute top-3 right-3 w-10 h-10 rounded-full bg-background/90 flex items-center justify-center hover:bg-destructive hover:text-white transition-colors"
                        data-testid={`unsave-${product.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="p-4">
                      <Link to={`/product/${product.id}`}>
                        <h3 className="font-heading font-semibold text-foreground hover:text-primary transition-colors line-clamp-1">
                          {product.name}
                        </h3>
                      </Link>
                      <p className="text-primary font-semibold mt-1">
                        ₹{product.price?.toLocaleString('en-IN')}
                      </p>
                      <Button
                        onClick={() => {
                          setSelectedProduct(product);
                          setIsModalOpen(true);
                        }}
                        className="w-full mt-3 rounded-full bg-primary hover:bg-primary/90"
                        data-testid={`preorder-saved-${product.id}`}
                      >
                        <ShoppingBag className="w-4 h-4 mr-2" />
                        Enquire Now
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />

      {selectedProduct && (
        <PreOrderModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedProduct(null);
          }}
          product={selectedProduct}
        />
      )}
    </div>
  );
};

export default SavedProducts;
